// Task status machine (spec §5.2). The API enforces it; the UI asks allowedActions() what to show.
import { hasPermission, isCoSupervisor, type Role } from './roles.ts';
import type { VerifyCheck } from './sod.ts';

export const TASK_STATUSES = [
  'not_started',
  'in_progress',
  'blocked',
  'done',
  'verified',
  'deferred',
  'retired',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_ACTIONS = [
  'start',
  'complete',
  'verify',
  'send-back',
  'block',
  'unblock',
  'defer',
  'resume',
  'reopen',
  'retire',
] as const;
export type TaskAction = (typeof TASK_ACTIONS)[number];

// worker = assignee, supervisor or owner; manager = supervisor or owner; verifier = canVerify() ok.
type Who = 'worker' | 'manager' | 'owner' | 'verifier' | 'pack';

interface Rule {
  readonly from: readonly TaskStatus[];
  readonly to: TaskStatus | 'previous';
  readonly who: Who;
  readonly needsReason: boolean;
}

const ACTIVE: readonly TaskStatus[] = ['not_started', 'in_progress'];

export const TASK_RULES: Readonly<Record<TaskAction, Rule>> = {
  start: { from: ['not_started'], to: 'in_progress', who: 'worker', needsReason: false },
  complete: { from: ['in_progress'], to: 'done', who: 'worker', needsReason: false },
  verify: { from: ['done'], to: 'verified', who: 'verifier', needsReason: false },
  'send-back': { from: ['done'], to: 'in_progress', who: 'verifier', needsReason: true },
  block: { from: ACTIVE, to: 'blocked', who: 'worker', needsReason: true },
  unblock: { from: ['blocked'], to: 'previous', who: 'manager', needsReason: true },
  defer: {
    from: ['not_started', 'in_progress', 'blocked', 'done'],
    to: 'deferred',
    who: 'owner',
    needsReason: true,
  },
  resume: { from: ['deferred'], to: 'not_started', who: 'owner', needsReason: false },
  reopen: { from: ['verified'], to: 'in_progress', who: 'owner', needsReason: true },
  retire: {
    from: ['not_started', 'in_progress', 'blocked', 'done', 'verified', 'deferred'],
    to: 'retired',
    who: 'pack',
    needsReason: true,
  },
};

export interface TransitionContext {
  readonly role: Role;
  readonly isAssignee: boolean;
  // Result of canVerify() for this user on this task; needed for verify and send-back.
  readonly verification?: VerifyCheck;
  // Status before the task was blocked; unblock returns to it.
  readonly prevStatus?: TaskStatus | null;
  // Reason or note typed by the user (or successor id / reason for a pack retirement).
  readonly reason?: string;
  // Required steps that are not ticked, by step number.
  readonly untickedRequiredSteps?: readonly number[];
  // Required evidence lines (codes) with no record yet.
  readonly missingEvidence?: readonly string[];
  // Retirement comes only from a content-pack import.
  readonly source?: 'user' | 'pack';
}

export type RefusalKind = 'forbidden' | 'conflict' | 'bad_request' | 'unprocessable';

export type RefusalCode =
  | 'invalid_transition'
  | 'not_allowed'
  | 'sod_violation'
  | 'reason_required'
  | 'steps_incomplete'
  | 'evidence_missing';

export interface Refusal {
  readonly ok: false;
  readonly code: RefusalCode;
  readonly kind: RefusalKind;
  readonly detail?: readonly (string | number)[];
}

export type TransitionResult = { readonly ok: true; readonly to: TaskStatus } | Refusal;

function refuse(
  code: RefusalCode,
  kind: RefusalKind,
  detail?: readonly (string | number)[],
): Refusal {
  return detail === undefined ? { ok: false, code, kind } : { ok: false, code, kind, detail };
}

// canVerify() already decides eligibility (designated verifier or co-supervisor) and SoD.
function checkVerifier(ctx: TransitionContext): Refusal | null {
  const v = ctx.verification;
  if (v === undefined || (!v.ok && v.reason === 'not_verifier')) {
    return refuse('not_allowed', 'forbidden');
  }
  if (!v.ok) return refuse('sod_violation', 'forbidden', v.conflicts);
  return null;
}

const WHO_CHECKS: Readonly<Record<Exclude<Who, 'verifier'>, (ctx: TransitionContext) => boolean>> =
  {
    pack: (ctx) => ctx.source === 'pack',
    owner: (ctx) => ctx.role === 'owner',
    manager: (ctx) => isCoSupervisor(ctx.role),
    worker: (ctx) =>
      isCoSupervisor(ctx.role) || (ctx.isAssignee && hasPermission(ctx.role, 'task.transition')),
  };

function checkWho(who: Who, ctx: TransitionContext): Refusal | null {
  if (who === 'verifier') return checkVerifier(ctx);
  return WHO_CHECKS[who](ctx) ? null : refuse('not_allowed', 'forbidden');
}

function checkRequirements(action: TaskAction, ctx: TransitionContext): Refusal | null {
  if (TASK_RULES[action].needsReason && (ctx.reason ?? '').trim() === '') {
    return refuse('reason_required', 'bad_request');
  }
  if (action !== 'complete') return null;
  const steps = ctx.untickedRequiredSteps ?? [];
  if (steps.length > 0) return refuse('steps_incomplete', 'unprocessable', steps);
  const evidence = ctx.missingEvidence ?? [];
  if (evidence.length > 0) return refuse('evidence_missing', 'unprocessable', evidence);
  return null;
}

function target(rule: Rule, ctx: TransitionContext): TaskStatus {
  if (rule.to !== 'previous') return rule.to;
  // A blocked task returns to where it was; anything unexpected falls back to In progress.
  return ctx.prevStatus === 'not_started' ? 'not_started' : 'in_progress';
}

// Checks the state edge, then who may act, then the action's requirements (reason, steps, evidence).
export function canTransition(
  from: TaskStatus,
  action: TaskAction,
  ctx: TransitionContext,
): TransitionResult {
  const rule = TASK_RULES[action];
  if (!rule.from.includes(from)) return refuse('invalid_transition', 'conflict');
  const who = checkWho(rule.who, ctx);
  if (who !== null) return who;
  const req = checkRequirements(action, ctx);
  if (req !== null) return req;
  return { ok: true, to: target(rule, ctx) };
}

// Actions this user may take now, ignoring inputs supplied with the action itself (reason, ticks,
// evidence): the server re-checks those when the action is posted.
export function allowedActions(from: TaskStatus, ctx: TransitionContext): TaskAction[] {
  return TASK_ACTIONS.filter((action) => {
    const rule = TASK_RULES[action];
    return rule.from.includes(from) && checkWho(rule.who, ctx) === null;
  });
}

export function isTaskStatus(value: string): value is TaskStatus {
  return (TASK_STATUSES as readonly string[]).includes(value);
}

export function isTaskAction(value: string): value is TaskAction {
  return (TASK_ACTIONS as readonly string[]).includes(value);
}
